import type { AdminOrgDto } from '@carcheck/shared';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CompaniesFilterBar } from './_components/CompaniesFilterBar';
import { buildCompanyColumns } from './_lib/companyColumns';
import { companyFiltersSchema, filterKeyOf, hasActiveFilters } from './_lib/companyFilters';
import { useAdminOrgsList } from '../../api/admin-orgs';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import { companyDetailPath, Routes } from '../../routes/routes';

/**
 * `/companies` — «Компании» (AUT-403 / CP-10b), after
 * `design/mockups/admin/companies-list.html`.
 *
 * Gated on `verify_inspectors` (the API's class-level permission) around
 * the component that OWNS the query, so an admin without it sends nothing.
 * The owner's name arrives masked without `view_pii`. Filters live in the
 * URL; the cursor API is bridged to the grid by `useCursorPagination`. A
 * row opens the company card; «Создать компанию» opens the create page.
 *
 * Not here: the header totals and the «Город» / «Скрыть демо» filters (no
 * server support), the «Отчётов за 30 дней» column (CP-05).
 */
const CompaniesListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, setFilters, reset } = useUrlFilters(companyFiltersSchema);
  const pager = useCursorPagination();

  const filterKey = filterKeyOf(filters);
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable; re-run only when the filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminOrgsList({
    status: filters.status,
    ...(filters.q ? { q: filters.q } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildCompanyColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
            {t('companies.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('companies.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(Routes.App.CompanyNew)}
        >
          {t('companies.create')}
        </Button>
      </Box>

      <CompaniesFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminOrgDto>
        rows={items}
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => navigate(companyDetailPath(row.id))}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('companies.empty')}
          </Typography>
        }
      />
    </Box>
  );
};

/** Whole-page gate: no `verify_inspectors` → the default 403 panel, no requests. */
export const CompaniesListPage = (): ReactElement => (
  <RequirePermission perm="verify_inspectors">
    <CompaniesListPageInner />
  </RequirePermission>
);
